import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Info, Activity } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useILScore } from '@/hooks/useILScore';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

interface ILDetailModalProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

export function ILDetailModal({ open, onClose, onRefresh }: ILDetailModalProps) {
  const { todayScore } = useILScore(7);
  const navigate = useNavigate();
  const { trackFeatureUsage } = useAnalytics();

  if (!todayScore) return null;

  const hasHRV = todayScore.hrv_score !== null;
  const factors = [
    { key: 'diet', label: 'Diet', score: todayScore.diet_score, info: 'Based on NOVA classification, sugar, fiber, additives, and omega-3 intake' },
    { key: 'sleep', label: 'Sleep', score: todayScore.sleep_score, info: 'Duration, quality, and consistency of your sleep patterns' },
    { key: 'stress', label: 'Stress', score: todayScore.stress_score, info: 'Self-reported stress and strain-recovery balance' },
    ...(hasHRV ? [{ key: 'hrv', label: 'HRV', score: todayScore.hrv_score!, info: 'Heart rate variability compared to your baseline (WHOOP users)' }] : []),
    { key: 'activity', label: 'Activity', score: todayScore.activity_score, info: 'Weekly moderate/vigorous activity and recovery balance' },
    { key: 'alcohol', label: 'Alcohol', score: todayScore.alcohol_score, info: 'Weekly alcohol units and binge episodes' }
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Your Inflammatory Load Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              <strong>What is this?</strong> A simple score from your food, sleep, stress, activity, HRV and alcohol patterns.
              This is an educational score based on lifestyle data, not a medical test. If symptoms persist, speak to your GP.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium">Contributing Factors</h3>
            {factors.map(factor => (
              <div key={factor.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium capitalize">{factor.label}</span>
                    {factor.key === 'hrv' && (
                      <Badge variant="outline" className="text-xs">WHOOP</Badge>
                    )}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="w-3 h-3 text-muted-foreground" aria-label={`${factor.label} info`} />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">{factor.info}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {Math.round(factor.score * 100)}%
                  </span>
                </div>
                <Progress 
                  value={factor.score * 100} 
                  className="h-2"
                  aria-label={`${factor.label} contribution ${Math.round(factor.score * 100)}%`}
                />
              </div>
            ))}
            
            {!hasHRV && (
              <Alert role="status" aria-live="polite">
                <Activity className="h-4 w-4" />
                <AlertDescription>
                  <strong>HRV not tracked:</strong> Connect WHOOP to unlock heart rate variability insights for more comprehensive inflammation tracking.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => {
                trackFeatureUsage('il_detail', 'start_plan_clicked', { score: todayScore.il_score });
                navigate('/longevity/inflammation');
                onClose();
              }}
              className="flex-1"
            >
              Start a 7-day plan
            </Button>
            <Button
              onClick={() => {
                trackFeatureUsage('il_detail', 'therappi_clicked', { score: todayScore.il_score });
                navigate('/therapy');
                onClose();
              }}
              variant="outline"
              className="flex-1"
            >
              Ask Therappi about this
            </Button>
          </div>

          {onRefresh && (
            <Button
              onClick={onRefresh}
              variant="ghost"
              size="sm"
              className="w-full"
            >
              Refresh Score
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
